import { memo, useCallback, useMemo, useState } from "react";
import classNames from "classnames";
import { Checkbox } from "../checkbox/checkbox";
import { Text } from "../text/text";
import { CloseIcon, FindItemIcon } from "../../assets/icons";

import styles from "./multiSelectList.module.css";

const MAX_VISIBLE_CHIPS = 4;

const defaultGetId = (item) => item.id;
const defaultGetName = (item) => item.name;

const SelectableRow = memo(({ id, name, checked, onToggle }) => {
    const handleChange = useCallback((next) => onToggle(id, next), [id, onToggle]);

    return (
        <Checkbox checked={checked} onChange={handleChange} className={styles.row}>
            <Text as="span" variant="body" className={styles.rowName}>
                {name}
            </Text>
        </Checkbox>
    );
});

SelectableRow.displayName = "SelectableRow";

const Chip = memo(({ id, name, onRemove }) => (
    <button
        type="button"
        className={styles.chip}
        onClick={() => onRemove(id, false)}
        aria-label={`Убрать ${name}`}
    >
        <Text as="span" variant="captionStrong" className={styles.chipName}>
            {name}
        </Text>
        <CloseIcon width={12} height={12} className={styles.chipIcon} />
    </button>
));

Chip.displayName = "Chip";

export const MultiSelectList = ({
    title,
    items,
    selectedIds,
    onToggle,
    onToggleMany,
    error = false,
    allLabel = "Выбрать все",
    searchPlaceholder = "Поиск по названию",
    emptyText = "Нет доступных вариантов",
    notFoundText = "Ничего не найдено",
    searchThreshold = 8,
    getId = defaultGetId,
    getName = defaultGetName,
}) => {
    const [query, setQuery] = useState("");
    const normalizedQuery = query.trim().toLowerCase();

    const visibleItems = useMemo(() => {
        if (!normalizedQuery) return items;
        return items.filter((item) => getName(item).toLowerCase().includes(normalizedQuery));
    }, [items, normalizedQuery, getName]);

    const visibleIds = useMemo(() => visibleItems.map(getId), [visibleItems, getId]);

    const selectedVisibleCount = useMemo(
        () => visibleIds.filter((id) => selectedIds.has(id)).length,
        [visibleIds, selectedIds],
    );

    const selectedItems = useMemo(
        () => items.filter((item) => selectedIds.has(getId(item))),
        [items, selectedIds, getId],
    );

    const handleToggleAll = useCallback(() => onToggleMany(visibleIds), [onToggleMany, visibleIds]);

    const withSearch = items.length >= searchThreshold;
    const allVisibleSelected = visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;
    const hiddenChipsCount = selectedItems.length - MAX_VISIBLE_CHIPS;

    return (
        <div className={styles.block}>
            <div className={styles.header}>
                <Text as="div" variant="bodyStrong">
                    {title}
                </Text>
                <Text as="div" variant="caption" className={styles.counter}>
                    {selectedIds.size} из {items.length}
                </Text>
            </div>

            {withSearch && (
                <label className={styles.search}>
                    <FindItemIcon width={20} height={20} className={styles.searchIcon} />
                    <input
                        type="text"
                        className={styles.searchInput}
                        placeholder={searchPlaceholder}
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                    />
                    {!!query && (
                        <CloseIcon
                            width={16}
                            height={16}
                            className={styles.searchClear}
                            onClick={() => setQuery("")}
                        />
                    )}
                </label>
            )}

            {withSearch && selectedItems.length > 0 && (
                <div className={styles.chips}>
                    {selectedItems.slice(0, MAX_VISIBLE_CHIPS).map((item) => (
                        <Chip
                            key={getId(item)}
                            id={getId(item)}
                            name={getName(item)}
                            onRemove={onToggle}
                        />
                    ))}
                    {hiddenChipsCount > 0 && (
                        <Text as="div" variant="captionStrong" className={styles.chipMore}>
                            ещё {hiddenChipsCount}
                        </Text>
                    )}
                </div>
            )}

            {items.length === 0 ? (
                <Text as="div" variant="body" className={styles.hint}>
                    {emptyText}
                </Text>
            ) : (
                <div className={classNames(styles.listWrapper, { [styles.listError]: error })}>
                    {visibleIds.length > 0 && (
                        <>
                            <Checkbox
                                checked={allVisibleSelected}
                                indeterminate={selectedVisibleCount > 0}
                                onChange={handleToggleAll}
                                className={styles.row}
                            >
                                <Text as="span" variant="bodyStrong">
                                    {normalizedQuery ? `Все найденные · ${visibleIds.length}` : allLabel}
                                </Text>
                            </Checkbox>
                            <div className={styles.separator} />
                        </>
                    )}

                    <div className={styles.list}>
                        {visibleIds.length === 0 ? (
                            <Text as="div" variant="body" className={styles.listHint}>
                                {notFoundText}
                            </Text>
                        ) : (
                            visibleItems.map((item) => (
                                <SelectableRow
                                    key={getId(item)}
                                    id={getId(item)}
                                    name={getName(item)}
                                    checked={selectedIds.has(getId(item))}
                                    onToggle={onToggle}
                                />
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
